/*************************************************************************
 * NarrativeRichEditor.js
 *
 * Shared rich-text narrative editor + the HTML helpers PE and our editor
 * agree on (bold / italic / underline + <br> line breaks). Used by both the
 * Edit Tray's narrative cells and the Bill Paras modal so formatting round-
 * trips identically everywhere.
 *************************************************************************/
import React, { useEffect } from "react";

// Escape when wrapping back into HTML. Non-breaking spaces ( , produced by
// the Tab-indent in the editor and by PE narratives) are emitted as the literal
// &nbsp; entity so the indentation is preserved verbatim in the FeeNarrative we
// send to PE. The   replacement runs AFTER the &→&amp; pass so the entity's
// own ampersand is not double-escaped.
export const escapeHtml = (str = "") =>
  String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
    .replace(/\u00a0/g, "&nbsp;");

// Sanitize narrative HTML down to the inline formatting our editor + PE agree
// on: bold / italic / underline + <br> line breaks. Text nodes are escaped;
// block tags (p/div) and the PE <font> wrapper are unwrapped (a block becomes a
// trailing <br>); any other tag is dropped to its text. Used BOTH to normalize
// PE HTML for the editor on load AND to serialize the editor HTML on save, so
// the two always round-trip to the same shape (keeps the change-highlight from
// firing on cosmetic markup differences).
//
// Emit <strong>/<em>/<u> (NOT <b>/<i>): PE's print/PDF renderer only honors the
// semantic tags — <b>/<i> render in the browser editor but get dropped on the
// printed draft. execCommand produces <b>/<i>, so we normalize them here.
export const sanitizeNarrativeHtml = (html) => {
  if (!html) return "";
  // DI change vs Botfield: parse with DOMParser (an inert document — no scripts run, no
  // resources load) instead of a live <div>.innerHTML, since stored narratives are untrusted.
  const root = new DOMParser().parseFromString(String(html), "text/html").body;
  const walk = (node) => {
    let out = "";
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        out += escapeHtml(child.nodeValue);
        return;
      }
      if (child.nodeType !== 1) return;
      const tag = child.tagName;
      if (tag === "BR") { out += "<br>"; return; }

      // Detect formatting from BOTH the tag name AND inline styles. execCommand
      // doesn't always use tags — depending on the browser/selection it may emit
      // <span style="text-decoration:underline"> (or font-weight/font-style),
      // which is why underline previously applied to only part of a selection.
      const style = child.style || {};
      const fw = String(style.fontWeight || "");
      const deco = String(style.textDecoration || style.textDecorationLine || "");
      const isBold      = tag === "B" || tag === "STRONG" || /bold/i.test(fw) || parseInt(fw, 10) >= 600;
      const isItalic    = tag === "I" || tag === "EM" || String(style.fontStyle || "") === "italic";
      const isUnderline = tag === "U" || /underline/.test(deco);

      let inner = walk(child);
      if (isUnderline) inner = `<u>${inner}</u>`;
      if (isItalic)    inner = `<em>${inner}</em>`;
      // Bold → plain <strong> (matches PE's native narrative format). With the
      // Arial font on the <p>, the cascade resolves <strong> to Arial Bold.
      if (isBold)      inner = `<strong>${inner}</strong>`;
      // Block-level elements imply a line break after their content.
      if (tag === "P" || tag === "DIV") inner += "<br>";
      out += inner;
    });
    return out;
  };
  // Collapse 3+ consecutive breaks to a double and trim leading/trailing ones.
  return walk(root).replace(/(<br>){3,}/g, "<br><br>").replace(/^(<br>)+|(<br>)+$/g, "");
};

// Escape plain text into editor HTML, preserving line breaks as <br> (used for
// Bill Paras standard-paragraph appends, which are plain text).
export const plainTextToEditorHtml = (text) =>
  escapeHtml(String(text || "")).replace(/\r\n|\r|\n/g, "<br>");

// Strip narrative HTML to plain text (kept for callers that need a text-only
// view, e.g. truncation/summaries).
export const stripNarrativeHtml = (html) => {
  if (!html) return "";
  return String(html)
    .replace(/<\/p>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&#160;/g, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+\n/g, "\n")
    .trim();
};

// One indent step, in pixels. Increase adds a step, decrease removes one and stops
// at 0 — there is no negative indent.
export const INDENT_STEP_PX = 20;

// Read an indent back off a stored narrative.
//
// Needed because the round-trip is lossy in one direction: the editor holds SANITIZED
// html (sanitizeNarrativeHtml strips every <p>, so the indent can't live in the edited
// value), and the wrapper <p> is only re-applied at save. So the indent is parsed out
// of the stored HTML at load and carried alongside the text from then on.
export const parseNarrativeIndent = (html) => {
  if (!html) return 0;
  const m = /margin-left\s*:\s*(-?\d+(?:\.\d+)?)\s*px/i.exec(String(html));
  if (!m) return 0;
  const px = Math.round(Number(m[1]));
  return Number.isFinite(px) && px > 0 ? px : 0;
};

// Wrap editor HTML into standard PE narrative HTML. The inner content keeps
// bold/italic/underline + <br> line breaks so formatting and breaks render in
// the generated PDFs. Returns "" when the narrative has no text.
//
// Font is set via a CSS style on the <p> (Arial 10pt), NOT the old <font> tag.
// The <font> tag pinned an exact face and stopped PE's PDF engine from
// switching to the bold variant; with a CSS font-family on the paragraph plus
// the inline font-weight on <strong>, the cascade resolves to Arial Bold.
//
// `indentPx` rides on the same <p> as margin-left. Indent is a property of the whole
// narrative line rather than of a selection, which matches the shape this function
// already produces: one paragraph with <br> breaks inside it, not a run of paragraphs.
export const wrapNarrativeHtml = (html, indentPx = 0) => {
  const inner = sanitizeNarrativeHtml(html);
  const probe = document.createElement("div");
  probe.innerHTML = inner;
  if (!(probe.textContent || "").trim()) return "";
  const indent = Number(indentPx) > 0 ? ` margin-left: ${Math.round(Number(indentPx))}px;` : "";
  return `<p style="font-family: Arial, Helvetica, sans-serif; font-size: 10pt;${indent}">${inner}</p>`;
};

// Walk up from `node` (exclusive of `boundary`) looking for an element with the
// given tag name. Used to detect (and toggle off) existing B/I/U formatting.
export function findAncestorTag(node, tagName, boundary) {
  let n = node;
  while (n && n !== boundary) {
    if (n.nodeType === 1 && n.tagName === tagName) return n;
    n = n.parentNode;
  }
  return null;
}

export const NARRATIVE_FORMAT_TAG = { bold: "STRONG", italic: "EM", underline: "U" };

// Rich-text narrative editor — bold / italic / underline + spell check.
// Uses contentEditable (a <textarea> can't hold formatting). Emits SANITIZED
// HTML so the stored value matches the load format. The editor's innerHTML is
// only rewritten when the value changes from OUTSIDE (Bill Paras / revert),
// never on a keystroke — that would reset the caret.
//
// Formatting (see `format` below) wraps a real selection in <strong>/<em>/<u>
// via the Range API for deterministic semantic tags, and falls back to
// execCommand for a collapsed caret so "click B then type" still works. emit()
// runs the editor's innerHTML through the tag-based sanitizer — all three
// formats are real elements, so they're captured identically.
//
// On focus the editor registers its `format` fn via onActivate so a shared
// toolbar can format whichever editor is being edited.
export function NarrativeRichEditor({ value, onChange, onActivate, onFormatChange, className, style }) {
  const ref = React.useRef(null);
  const lastHtmlRef = React.useRef(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const v = value || "";
    if (v !== lastHtmlRef.current) {
      el.innerHTML = v;
      lastHtmlRef.current = v;
    }
  }, [value]);

  const emit = () => {
    const html = sanitizeNarrativeHtml(ref.current?.innerHTML ?? "");
    lastHtmlRef.current = html;
    onChange(html);
  };

  // ── Line-level indent ─────────────────────────────────────────────────────
  //
  // Indents/outdents ONLY the line the caret is on, by inserting or removing a 4-NBSP
  // run at that line's start. A "line" is the run between <br>s — the narrative is saved
  // as ONE <p> with <br> breaks inside it, so that is the only line concept there is.
  //
  // Why NBSP and not margin-left: margin-left lives on the wrapper <p>, so it can only
  // ever indent the WHOLE narrative. Indenting line 1 and then outdenting line 2 is not
  // expressible that way — the second action just undoes the first. NBSP is per-line and
  // is already how this editor's Tab key indents (and how PE's own narratives do it);
  // sanitizeNarrativeHtml preserves the characters verbatim.
  //
  // Returns true when something actually changed, so the caller can fall back to the
  // legacy row-level indent on narratives that still carry one.
  const indentLine = (direction) => {
    const el = ref.current;
    if (!el) return false;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return false;

    // Every text node and <br>, in document order.
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
    const nodes = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeType === 3 || n.nodeName === "BR") nodes.push(n);
    }

    // Where the caret sits in that list. An element container means the caret is
    // between children, so resolve it to the child it precedes.
    let caretNode = range.startContainer;
    if (caretNode.nodeType === 1) {
      caretNode = caretNode.childNodes[range.startOffset] || caretNode.lastChild || caretNode;
    }
    let caretIdx = nodes.indexOf(caretNode);
    if (caretIdx < 0) {
      // Caret inside a node we didn't collect (e.g. an empty <strong>) — find the last
      // collected node that precedes it in document order.
      caretIdx = nodes.length - 1;
      for (let i = 0; i < nodes.length; i++) {
        const pos = caretNode.compareDocumentPosition(nodes[i]);
        // eslint-disable-next-line no-bitwise
        if (pos & Node.DOCUMENT_POSITION_FOLLOWING) { caretIdx = i - 1; break; }
      }
      if (caretIdx < 0) caretIdx = 0;
    }

    // The line starts after the nearest <br> at or before the caret.
    let startIdx = 0;
    for (let i = Math.min(caretIdx, nodes.length - 1); i >= 0; i--) {
      if (nodes[i].nodeName === "BR") { startIdx = i + 1; break; }
    }
    // First text node on this line (stop at the next <br> — an empty line has none).
    let target = null;
    for (let i = startIdx; i < nodes.length; i++) {
      if (nodes[i].nodeName === "BR") break;
      if (nodes[i].nodeType === 3) { target = nodes[i]; break; }
    }

    const NBSP = " ";
    const STEP = 4;
    let changed = false;

    if (direction === "increase") {
      if (target) {
        target.nodeValue = NBSP.repeat(STEP) + (target.nodeValue || "");
        if (range.startContainer === target) {
          const r = document.createRange();
          r.setStart(target, range.startOffset + STEP);
          r.collapse(true);
          sel.removeAllRanges(); sel.addRange(r);
        }
      } else {
        // Empty line — create the run and drop the caret after it.
        const node = document.createTextNode(NBSP.repeat(STEP));
        const anchor = nodes[startIdx];
        if (anchor) anchor.parentNode.insertBefore(node, anchor);
        else el.appendChild(node);
        const r = document.createRange();
        r.setStartAfter(node);
        r.collapse(true);
        sel.removeAllRanges(); sel.addRange(r);
      }
      changed = true;
    } else {
      if (!target) return false;
      const text = target.nodeValue || "";
      let remove = 0;
      while (remove < STEP && text.charCodeAt(remove) === 0x00a0) remove++;
      if (!remove) return false;
      target.nodeValue = text.slice(remove);
      if (range.startContainer === target) {
        const r = document.createRange();
        r.setStart(target, Math.max(0, range.startOffset - remove));
        r.collapse(true);
        sel.removeAllRanges(); sel.addRange(r);
      }
      changed = true;
    }

    if (changed) emit();
    return changed;
  };

  // Apply bold/italic/underline. With a real selection we wrap it in the
  // semantic tag ourselves (deterministic <strong>/<em>/<u>, toggle on/off).
  // With just a caret (no selection) we fall back to execCommand so the user
  // can "click B, then type" — emit() captures whatever it produces.
  const format = (cmd) => {
    const el = ref.current;
    const tagName = NARRATIVE_FORMAT_TAG[cmd];
    if (!el || !tagName) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.commonAncestorContainer)) return;

    if (range.collapsed) {
      // No selection — toggle the pending format for subsequent typing.
      try { document.execCommand("styleWithCSS", false, false); } catch { /* unsupported */ }
      document.execCommand(cmd, false, null);
      emit();
      if (onFormatChange) onFormatChange();
      return;
    }

    // Detect an existing wrapper from the selection's COMMON ancestor first —
    // that's robust when the whole selection sits inside the formatting element
    // (the usual "select the formatted text and toggle it off" case, which the
    // start-container-only check missed, so underline couldn't be un-checked).
    // Fall back to the start container for partial selections.
    const commonEl = range.commonAncestorContainer.nodeType === 1
      ? range.commonAncestorContainer
      : range.commonAncestorContainer.parentNode;
    const startEl = range.startContainer.nodeType === 1
      ? range.startContainer
      : range.startContainer.parentNode;
    const existing =
      findAncestorTag(commonEl, tagName, el) || findAncestorTag(startEl, tagName, el);

    if (existing) {
      // Toggle OFF — unwrap the formatting element in place.
      const parent = existing.parentNode;
      while (existing.firstChild) parent.insertBefore(existing.firstChild, existing);
      parent.removeChild(existing);
    } else {
      // Toggle ON — wrap the selection in the semantic tag.
      const wrapper = document.createElement(tagName);
      try {
        range.surroundContents(wrapper);
      } catch {
        // surroundContents throws when the range crosses element boundaries —
        // extract + wrap + re-insert handles that case.
        const frag = range.extractContents();
        wrapper.appendChild(frag);
        range.insertNode(wrapper);
      }
      sel.removeAllRanges();
      const r = document.createRange();
      r.selectNodeContents(wrapper);
      sel.addRange(r);
    }
    el.normalize();
    emit();
    if (onFormatChange) onFormatChange();
  };

  // Ctrl/Cmd + B / I / U shortcuts, plus Tab / Shift+Tab for indentation.
  const onKeyDown = (e) => {
    // ── 4c: Tab inserts 4 non-breaking spaces (&nbsp;); Shift+Tab outdents ────
    // We use the NBSP character ( ) ×4 rather than a tab so the indent is
    // preserved in the saved HTML and renders identically everywhere.
    if (e.key === "Tab") {
      e.preventDefault();
      const el = ref.current;
      if (!el) return;
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      if (!el.contains(range.commonAncestorContainer)) return;

      const NBSP = " ";
      const INDENT = NBSP.repeat(4);

      if (e.shiftKey) {
        // Shift+Tab — remove up to 4 NBSP characters immediately before the caret.
        const node = range.startContainer;
        if (node.nodeType === 3 /* TEXT_NODE */) {
          const offset = range.startOffset;
          const text   = node.nodeValue || "";
          let remove = 0;
          while (remove < 4 && offset - 1 - remove >= 0 &&
                 text.charCodeAt(offset - 1 - remove) === 0x00a0) {
            remove++;
          }
          if (remove > 0) {
            node.nodeValue = text.slice(0, offset - remove) + text.slice(offset);
            const newRange = document.createRange();
            newRange.setStart(node, offset - remove);
            newRange.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange);
          }
        }
      } else {
        // Tab — insert 4 NBSP characters at the caret.
        range.deleteContents();
        const indentNode = document.createTextNode(INDENT);
        range.insertNode(indentNode);
        const newRange = document.createRange();
        newRange.setStartAfter(indentNode);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
      }
      emit();
      return;
    }

    // ── Ctrl/Cmd + B / I / U shortcuts ──────────────────────────────────────
    if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
    const k = (e.key || "").toLowerCase();
    const cmd = k === "b" ? "bold" : k === "i" ? "italic" : k === "u" ? "underline" : null;
    if (!cmd) return;
    e.preventDefault();
    format(cmd);
  };

  return (
    <div
      ref={ref}
      className={className || "ed-input ed-input--textarea edtray__rte-area"}
      // Lets the host show the line's indent while editing, so what you see in the
      // tray matches what the saved <p style="margin-left:…"> will render.
      style={style}
      contentEditable
      suppressContentEditableWarning
      spellCheck={true}
      lang="en-US"
      onInput={emit}
      onBlur={emit}
      // Second arg is the line-indent fn. Consumers that only destructure the first
      // (Bill Paras) are unaffected.
      onFocus={() => onActivate && onActivate(format, indentLine)}
      onKeyDown={onKeyDown}
    />
  );
}
